import { Link } from "react-router-dom";
import { motion } from "framer-motion";

function stockSummary(product) {
  const tracked = (product.variants || []).filter(v => typeof v.stock === "number");
  if (!tracked.length) return null;
  const left = tracked.reduce((s, v) => s + Math.max(0, v.stock), 0);
  const total = tracked.reduce((s, v) => s + (v.batch_size || 0), 0);
  const lowest = Math.min(...tracked.map(v => Math.max(0, v.stock)));
  const urgent = lowest > 0 && lowest < 5;
  return { left, total, soldOut: left === 0, urgent, lowest, low: urgent || (left > 0 && left <= Math.max(5, Math.round(total * 0.1))) };
}

const CARD_INITIAL = { opacity: 0, y: 24 };
const CARD_IN_VIEW = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true, margin: "-80px" };
const EASE = [0.16, 1, 0.3, 1];

export default function ProductCard({ product, index = 0 }) {
  const tested = product.testing_status === "verified";
  const stock = stockSummary(product);
  return (
    <motion.div
      data-testid={`product-card-${product.slug}`}
      initial={CARD_INITIAL}
      whileInView={CARD_IN_VIEW}
      viewport={CARD_VIEWPORT}
      transition={{ duration: 0.7, delay: (index % 6) * 0.04, ease: EASE }}
      className="group relative"
    >
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden border border-white/5 bg-[#050508]">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
              style={{ filter: "brightness(1.05) contrast(1.08) saturate(1.1)" }}
            />
          ) : (
            <div className="absolute inset-0 bl-vial-placeholder flex items-center justify-center">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/60">{product.name}</div>
            </div>
          )}

          {/* Green TESTED badge only */}
          {tested && (
            <div
              data-testid={`tested-badge-${product.slug}`}
              className="absolute top-3 right-3 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bl-green)] bg-black/55 backdrop-blur-sm border border-[color:var(--bl-green)]/50 px-2.5 py-1"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--bl-green)]" style={{ boxShadow: "0 0 8px var(--bl-green-soft)" }} />
              Tested
            </div>
          )}

          {stock && (
            <div
              data-testid={`stock-badge-${product.slug}`}
              className={`absolute bottom-3 left-3 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase bg-black/60 backdrop-blur-sm border px-2.5 py-1 ${
                stock.soldOut ? "text-red-300 border-red-400/40" : stock.low ? "text-[color:var(--bl-gold)] border-[color:var(--bl-gold)]/50" : "text-white/80 border-white/20"}`}
            >
              {stock.soldOut ? "Sold out"
                : stock.urgent ? <><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--bl-gold)] animate-pulse" /> Only {stock.lowest} {product.kind === "peptide" ? "pack" : "unit"}{stock.lowest === 1 ? "" : "s"} left — selling fast</>
                : <>{stock.left}{stock.total ? ` / ${stock.total}` : ""} {product.kind === "peptide" ? "packs" : "units"} left</>}
            </div>
          )}

          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            style={{ background: "radial-gradient(circle at 50% 60%, rgba(0,229,255,0.14), transparent 65%)" }} />
        </div>

        {/* Name + price row */}
        <div className="mt-4 flex items-baseline justify-between gap-4">
          <h3 className="font-display uppercase tracking-tight text-lg text-white group-hover:text-[color:var(--bl-blue)] transition-colors">
            {product.name}
          </h3>
          <div className="text-right shrink-0">
            {product.from_price == null ? (
              <div data-testid={`contact-price-${product.slug}`} className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--bl-gold)]">Contact for availability</div>
            ) : (<>
            {product.from_price_original && product.from_price_original !== product.from_price && (
              <div className="font-mono text-[11px] text-white/40 line-through">A${product.from_price_original.toFixed(2)}</div>
            )}
            <div className="font-display text-lg text-white">A${product.from_price.toFixed(2)}</div>
            </>)}
          </div>
        </div>

        {/* Compact meta row — category · pack · strength count */}
        <div className="mt-2 flex items-center gap-2 flex-wrap font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">
          <span>{product.category}</span>
          <span className="text-white/20">/</span>
          <span>{product.kind === "peptide" ? "10 Vials" : product.kind === "consumable" ? "Diluent" : "Hardware"}</span>
          <span className="text-white/20">/</span>
          <span>{product.strengths.length} {product.kind === "peptide" ? (product.strengths.length === 1 ? "Strength" : "Strengths") : (product.strengths.length === 1 ? "Option" : "Options")}</span>
        </div>
      </Link>
    </motion.div>
  );
}
